import React from 'react';
import { Link } from 'react-router-dom';
import { GuidLink } from './GuidLink';
import { getOptionSetLabel } from '../../lib/option-sets';
import { displayValue } from '../../lib/format-value';
import type { ColumnConfig } from '../../lib/entity-config';

interface CuroAttribute {
  Key: string;
  Value: any;
}

interface CuroRecord {
  Id: string;
  LogicalName: string;
  Attributes: CuroAttribute[];
}

interface EntityDetailProps {
  record: CuroRecord;
  columns: ColumnConfig[];
  entityName: string;
  displayName: string;
  onDelete?: () => void;
}

function formatValue(value: any, column: ColumnConfig): React.ReactNode {
  if (value === null || value === undefined) return <span className="text-slate-400">-</span>;

  switch (column.type) {
    case 'date':
      try {
        return new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
      } catch {
        return displayValue(value);
      }
    case 'number':
      return typeof value === 'number'
        ? value.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
        : displayValue(value);
    case 'boolean':
      return value ? 'Yes' : 'No';
    case 'optionSet':
      if (typeof value === 'object' && value !== null && 'Value' in value) {
        return getOptionSetLabel(column.optionSetKey ?? column.key, value.Value);
      }
      return typeof value === 'number' ? getOptionSetLabel(column.optionSetKey ?? column.key, value) : displayValue(value);
    case 'fk':
      if (typeof value === 'object' && value !== null && 'Id' in value) {
        return <GuidLink id={value.Id} name={value.Name} entityName={column.targetEntity ?? ''} />;
      }
      return displayValue(value);
    case 'textarea':
      return <div className="whitespace-pre-wrap">{displayValue(value)}</div>;
    default:
      return displayValue(value);
  }
}

export function EntityDetail({ record, columns, entityName, displayName, onDelete }: EntityDetailProps) {
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to={`/${entityName}`} className="text-sm text-blue-600 hover:text-blue-800 mb-1 inline-block">
            Back to {displayName} list
          </Link>
          <h2 className="text-xl font-semibold text-slate-900">
            {displayName} Details
          </h2>
        </div>
        <div className="flex gap-2">
          <Link
            to={`/${entityName}/${record.Id}/edit`}
            className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
          >
            Edit
          </Link>
          {onDelete && (
            <button
              onClick={onDelete}
              className="px-4 py-2 text-sm rounded-md border border-red-300 text-red-600 hover:bg-red-50"
            >
              Delete
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <div className="px-6 py-3 bg-slate-50 border-b border-slate-100">
          <span className="text-xs text-slate-500 font-mono">ID: {record.Id}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2">
          {record.Attributes.map(attr => {
            const col = columns.find(c => c.key === attr.Key);
            if (!col) {
              return (
                <div key={attr.Key} className="px-6 py-3 border-b border-slate-100">
                  <div className="text-sm font-medium text-slate-500 mb-0.5">{attr.Key}</div>
                  <div className="text-sm text-slate-900">{JSON.stringify(attr.Value)}</div>
                </div>
              );
            }
            return (
              <div key={attr.Key} className="px-6 py-3 border-b border-slate-100">
                <div className="text-sm font-medium text-slate-500 mb-0.5">{col.label}</div>
                <div className="text-sm text-slate-900">{formatValue(attr.Value, col)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
